import {
  IonButton,
  IonIcon,
  IonListHeader,
  IonText,
  useIonModal,
  useIonRouter,
} from '@ionic/react';
import { funnel, searchOutline } from 'ionicons/icons';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import Layout, { ButtonType } from '@components/Layout';
import List from '@components/List';
import SessionItem from '@components/SessionItem';
import RoutePath from '@custom-types/RoutePath';
import { Session, SessionGroup, HAPPENING, UPCOMING } from '@custom-types/Session';
import { CheckAction } from '@pages/Check/Enums';
import { generateRoute } from '@services/navigation-service';
import { setSessionInfo } from '@state/session';
import { getLocale } from '@state/system';

import FilterTracksModal from './FilterTracksModal';
import { useSessions, useTracks } from './queries';

const EmptyItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px;
`;

const SearchIcon = styled(IonIcon)`
  width: 160px;
  height: 160px;
`;

const EmptyText = styled(IonText)`
  font-size: 24px;
`;

const GROUP_MAPS = (key: SessionGroup) =>
  ({
    [HAPPENING]: t`Current`,
    [UPCOMING]: t`Upcoming`,
  }[key]);

const useTracksModal = (eventId: string) => {
  const { data: tracks } = useTracks(eventId);
  const [selectedTracks, setSelectedTracks] = useState<string[]>([]);

  const [present, dismiss] = useIonModal(FilterTracksModal, {
    onDismiss: () => dismiss(),
    tracks,
    selected: selectedTracks,
    onApply: setSelectedTracks,
  });

  return { present, selectedTracks };
};

const Sessions = () => {
  const { push: pushRoute, goBack } = useIonRouter();
  const dispatch = useDispatch();
  const { eventId, goBack: goBackRaw } = useParams<{ eventId: string; goBack: string }>();
  // This comes in as a string, so we need to convert it to a boolean and default to false
  const shouldGoBack = goBackRaw === 'true';
  const locale = useSelector(getLocale);
  const { present, selectedTracks } = useTracksModal(eventId);
  const [searchValue, setSearchValue] = useState('');

  const {
    fetchNextPage,
    data,
    hasNextPage = false,
    isLoading,
    isFetching,
  } = useSessions({ eventId, selectedTracks, searchValue });

  const onSessionClick = useCallback(
    (session: Session) => {
      dispatch(setSessionInfo({ ...session }));
      if (shouldGoBack) {
        goBack();
      } else {
        pushRoute(
          generateRoute(RoutePath.SessionCheck, {
            eventId,
            sessionId: session.id,
            action: CheckAction.CheckIn,
          })
        );
      }
    },
    [dispatch, shouldGoBack, goBack, pushRoute, eventId]
  );

  const populateItem = useCallback(
    (session: Session, showGroup?: boolean) => (
      <React.Fragment key={session.id}>
        {showGroup && (
          <IonListHeader key={session.group}>{GROUP_MAPS(session.group)}</IonListHeader>
        )}
        <SessionItem
          key={session.id}
          locale={locale}
          // skipcq: JS-0417
          onClick={() => onSessionClick(session)}
          session={session}
        />
      </React.Fragment>
    ),
    [locale, onSessionClick]
  );

  const openModal = useCallback(() => present(), [present]);

  let currentGroup: string;

  return (
    <Layout title={t`Select Session`} buttonType={ButtonType.Back}>
      <List
        loadData={fetchNextPage}
        // skipcq: JS-0417
        onSearch={value => setSearchValue(value)}
        endOfList={!hasNextPage}
        buttons={
          <IonButton
            color="medium"
            // skipcq: JS-0417
            onClick={openModal}
          >
            <IonIcon icon={funnel} />
          </IonButton>
        }
        isLoading={isLoading && isFetching}
      >
        {data && data.pages[0].data.length === 0 && (
          <EmptyItem>
            <SearchIcon size="large" icon={searchOutline} color="warning" />
            <EmptyText color="dark">{t`No Results Found`}</EmptyText>
          </EmptyItem>
        )}
        {data &&
          data?.pages.map(({ data: sessions }) =>
            sessions.map(session => {
              let showGroup = false;
              if (currentGroup !== session.group) {
                showGroup = true;
                currentGroup = session.group;
              }
              return populateItem(session, showGroup);
            })
          )}
      </List>
    </Layout>
  );
};

export default Sessions;
